/**
 * O período dos números do Início e da Visão geral (2026-10-19).
 *
 * O resumo são N dias INTEIROS terminando no dia do ÚLTIMO DADO da empresa
 * (`last_day`), e não em hoje — decisão da dona: se a última menção é do
 * dia 10 e hoje é 15, "1 dia" mostra o dia 10. Por isso a tela PRECISA
 * dizer de que dias são os números; é isso que este arquivo escreve.
 *
 * Datas em texto, sem `Date`: `first_day`/`last_day` são dias e
 * `latest_published_at` é hora de parede (a convenção de `published_at`,
 * sem fuso) — os dígitos são o relógio e não se convertem. O "hoje" que
 * entra na comparação é o da instalação (`todayInInstallation`).
 */

import type { MentionSummary } from "@/shared/api/api-client";

/** As escolhas do seletor, em dias. */
export const SUMMARY_PERIODS = [1, 7, 15, 30] as const;
export type SummaryPeriod = (typeof SUMMARY_PERIODS)[number];
export const DEFAULT_SUMMARY_PERIOD: SummaryPeriod = 30;

export function summaryPeriodLabel(days: number): string {
  return days === 1 ? "1 dia" : `${days} dias`;
}

/** "2026-10-04" → "04/10/2026", pelos dígitos. */
function dayText(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

/** "2026-10-10T14:32:05" → "14:32", pelos dígitos. */
function timeText(wallClock: string): string {
  return wallClock.slice(11, 16);
}

/** A frase de cima dos números. `today` é "AAAA-MM-DD" da instalação. */
export function summaryWindowText(summary: MentionSummary, today: string): string {
  if (summary.latest_published_at === null) {
    return "Ainda não há menções desta empresa; quando houver, os números contam até o dia do dado mais recente.";
  }
  const range =
    summary.window_days === 1
      ? `do dia ${dayText(summary.last_day)}, o dia do dado mais recente`
      : `dos últimos ${summary.window_days} dias com dado: de ${dayText(summary.first_day)} a ${dayText(summary.last_day)}`;
  const latest = `O dado mais recente é de ${dayText(summary.latest_published_at)} às ${timeText(summary.latest_published_at)}.`;
  const stale =
    summary.last_day < today
      ? " Não há menções depois disso: o período termina no último dado, não hoje."
      : "";
  return `Os indicadores são ${range}. ${latest}${stale}`;
}

/** O rótulo curto, ao lado do nome da empresa: "7 dias · até 10/10/2026". */
export function summaryWindowShort(summary: MentionSummary): string {
  if (summary.latest_published_at === null) return summaryPeriodLabel(summary.window_days);
  return summary.window_days === 1
    ? `1 dia · ${dayText(summary.last_day)}`
    : `${summary.window_days} dias · até ${dayText(summary.last_day)}`;
}
